import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  AdminMetricsResponse,
  AdminUserDetail,
  AdminUsersListResponse,
  CreateAdminUserBody,
  CreateAdminUserResponse,
  UpdateAdminUserBody,
} from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

const ADMIN_KEY = "admin" as const;

export interface AdminUsersParams {
  search?: string;
  status?: "active" | "suspended";
  role?: "admin" | "user";
  /** 1-based page index for offset pagination. */
  page?: number;
  limit?: number;
}

function buildUsersQuery(params: AdminUsersParams): string {
  const sp = new URLSearchParams();
  if (params.search) sp.set("search", params.search);
  if (params.status) sp.set("status", params.status);
  if (params.role) sp.set("role", params.role);
  if (params.page && params.page > 1) sp.set("page", String(params.page));
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export function useAdminMetrics() {
  return useQuery<AdminMetricsResponse, ApiClientError>({
    queryKey: [ADMIN_KEY, "metrics"],
    queryFn: () => apiFetch<AdminMetricsResponse>("/admin/metrics"),
  });
}

export function useAdminUsers(params: AdminUsersParams = {}) {
  return useQuery<AdminUsersListResponse, ApiClientError>({
    // params in the key so changing search/filter/page refetches.
    queryKey: [ADMIN_KEY, "users", "list", params],
    queryFn: () =>
      apiFetch<AdminUsersListResponse>(`/admin/users${buildUsersQuery(params)}`),
    // Keep the prior page's rows during a page change so the height holds and
    // the new rows fade in rather than flashing a skeleton.
    placeholderData: keepPreviousData,
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery<AdminUserDetail, ApiClientError>({
    queryKey: [ADMIN_KEY, "users", "detail", id],
    queryFn: () => apiFetch<AdminUserDetail>(`/admin/users/${id}`),
    enabled: Boolean(id),
  });
}

/** Invalidate every cached admin query — used after any user mutation since
 *  counts/metrics/list ordering can all shift. */
function invalidateAdmin(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [ADMIN_KEY] });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation<CreateAdminUserResponse, ApiClientError, CreateAdminUserBody>({
    mutationFn: (body) =>
      apiFetch<CreateAdminUserResponse>("/admin/users", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateAdmin(qc),
  });
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation<
    AdminUserDetail,
    ApiClientError,
    { id: string; body: UpdateAdminUserBody }
  >({
    mutationFn: ({ id, body }) =>
      apiFetch<AdminUserDetail>(`/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (user) => {
      qc.setQueryData([ADMIN_KEY, "users", "detail", user.id], user);
      invalidateAdmin(qc);
    },
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserDetail, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<AdminUserDetail>(`/admin/users/${id}/suspend`, { method: "POST" }),
    onSuccess: (user) => {
      qc.setQueryData([ADMIN_KEY, "users", "detail", user.id], user);
      invalidateAdmin(qc);
    },
  });
}

export function useUnsuspendUser() {
  const qc = useQueryClient();
  return useMutation<AdminUserDetail, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<AdminUserDetail>(`/admin/users/${id}/unsuspend`, { method: "POST" }),
    onSuccess: (user) => {
      qc.setQueryData([ADMIN_KEY, "users", "detail", user.id], user);
      invalidateAdmin(qc);
    },
  });
}

export function useDeleteAdminUser() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: [ADMIN_KEY, "users", "detail", id] });
      invalidateAdmin(qc);
    },
  });
}
